import { watch } from 'vue'

const SUPPORTED = ['en', 'ja'] as const
type SupportedLocale = (typeof SUPPORTED)[number]

const STORAGE_KEY = 'user-locale'
// Same key `detectBrowserLanguage` reads, so the server-side redirect on
// non-prefixed routes honors the saved language.
const COOKIE_KEY = 'i18n_redirected'

const isSupported = (value: unknown): value is SupportedLocale =>
  typeof value === 'string' && (SUPPORTED as readonly string[]).includes(value)

/**
 * Persists the user's language choice so it survives reloads and direct visits
 * to non-prefixed URLs (e.g. the landing page at `/`).
 *
 * Two stores are kept in sync:
 *  - `localStorage` — durable "browser memory" backup that outlives the cookie.
 *  - the `i18n_redirected` cookie — read by the server so it can redirect a
 *    non-prefixed route to the preferred locale before the page renders.
 */
export default defineNuxtPlugin((nuxtApp) => {
  // Persistence is a client concern; the cookie drives the server redirect.
  if (!process.client) return

  const i18n = nuxtApp.$i18n as {
    locale: { value: string }
    setLocale?: (locale: string) => unknown
  }

  // Long-lived (1 year) cookie so the preference genuinely sticks.
  const localeCookie = useCookie<string | null>(COOKIE_KEY, {
    maxAge: 60 * 60 * 24 * 365,
    path: '/',
    sameSite: 'lax'
  })

  const persist = (locale: string) => {
    if (!isSupported(locale)) return
    localStorage.setItem(STORAGE_KEY, locale)
    localeCookie.value = locale
  }

  // Restore the saved preference. The cookie wins (the server already used it
  // for the initial render); fall back to localStorage so a cleared cookie
  // still restores the language from the browser's memory.
  const stored = localStorage.getItem(STORAGE_KEY)
  const preferred = isSupported(localeCookie.value)
    ? localeCookie.value
    : isSupported(stored)
      ? stored
      : i18n.locale.value

  if (isSupported(preferred)) {
    persist(preferred)
    // Apply it on the current load if the route resolved to a different locale
    // (e.g. cookie was cleared but localStorage remembered the choice).
    if (preferred !== i18n.locale.value && typeof i18n.setLocale === 'function') {
      i18n.setLocale(preferred)
    }
  }

  // Keep both stores in sync whenever the language changes — via the header
  // selector or anywhere else.
  watch(
    () => i18n.locale.value,
    (newLocale) => persist(newLocale)
  )
})
